import React, { useMemo, useState } from 'react';
import {
    Badge,
    Button,
    Card,
    Collapse,
    Empty,
    Space,
    Tag,
    Timeline,
    Tooltip,
    Typography,
} from 'antd';
import {
    BulbOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    CopyOutlined,
    FlagOutlined,
    RobotOutlined,
    ToolOutlined,
} from '@ant-design/icons';

const { Text, Paragraph } = Typography;
const { Panel } = Collapse;

const FINAL_ANSWER_MARKER = '__final_answer__';

const TEXT = {
    title: 'AI \u63a8\u7406\u8fc7\u7a0b',
    empty: '\u672c\u6b21\u8bc6\u522b\u672a\u8c03\u7528\u4efb\u4f55\u5de5\u5177',
    iterations: '\u8f6e\u6b21',
    toolCalls: '\u5de5\u5177\u8c03\u7528',
    reasoning: '\u601d\u8003\u8fc7\u7a0b',
    finalReasoning: '\u6700\u7ec8\u601d\u8003',
    arguments: '\u5165\u53c2',
    result: '\u8fd4\u56de',
    error: '\u9519\u8bef',
    copied: '\u5df2\u590d\u5236',
    copy: '\u590d\u5236',
    expandAll: '\u5168\u90e8\u5c55\u5f00',
    collapseAll: '\u5168\u90e8\u6298\u53e0',
    iteration: '\u8f6e',
    finalAnswer: '\u6700\u7ec8\u56de\u590d',
};


const ITERATION_COLORS = ['#1677ff', '#722ed1', '#13c2c2', '#52c41a', '#fa8c16', '#eb2f96', '#a0d911'];


const tryCopy = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
    } catch (err) {
        // 静默：浏览器没授权 clipboard 时不要影响主流程
    }
};


const formatJson = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'string') return value;
    try {
        return JSON.stringify(value, null, 2);
    } catch (err) {
        return String(value);
    }
};


const CodeBlock = ({ value, maxHeight = 240 }) => {
    const text = formatJson(value);
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        await tryCopy(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
    };

    return (
        <div style={{ position: 'relative' }}>
            <pre
                style={{
                    margin: 0,
                    padding: '8px 32px 8px 10px',
                    background: '#fafafa',
                    border: '1px solid #f0f0f0',
                    borderRadius: 4,
                    fontFamily: 'Menlo, Consolas, "Courier New", monospace',
                    fontSize: 12,
                    lineHeight: 1.55,
                    maxHeight,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                }}
            >
                {text}
            </pre>
            <Tooltip title={copied ? TEXT.copied : TEXT.copy}>
                <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={handleCopy}
                    style={{ position: 'absolute', top: 4, right: 4 }}
                />
            </Tooltip>
        </div>
    );
};


/**
 * 把 trace 数组按 iteration 聚合：同一轮的多个 tool call 共用同一段 thinking。
 *
 * 输入：[{iteration:0,tool_name:'verify_aircraft_reg',thinking:'...',...},
 *        {iteration:0,tool_name:'verify_airport',thinking:'...',...},
 *        {iteration:1,tool_name:'__final_answer__',thinking:'...',is_final:true}]
 *
 * 输出：[{iteration:0, thinking:'...', calls:[entry1, entry2]},
 *        {iteration:1, thinking:'...', isFinal:true, calls:[entry3]}]
 */
const groupTraceByIteration = (trace = []) => {
    const groups = [];
    let current = null;
    trace.forEach((entry) => {
        if (!current || current.iteration !== entry.iteration) {
            current = {
                iteration: entry.iteration,
                thinking: entry.thinking || null,
                isFinal: !!entry.is_final,
                calls: [],
            };
            groups.push(current);
        } else {
            if (entry.thinking && !current.thinking) {
                current.thinking = entry.thinking;
            }
            if (entry.is_final) current.isFinal = true;
        }
        if (entry.tool_name && entry.tool_name !== FINAL_ANSWER_MARKER) {
            current.calls.push(entry);
        }
    });
    return groups;
};


const ReasoningBlock = ({ thinking, isFinal = false }) => {
    if (!thinking) return null;
    return (
        <div
            style={{
                background: isFinal ? '#f6ffed' : '#fffbe6',
                border: `1px solid ${isFinal ? '#b7eb8f' : '#ffe58f'}`,
                borderRadius: 4,
                padding: '8px 10px',
                marginBottom: 8,
            }}
        >
            <Space size={6} style={{ marginBottom: 4 }}>
                {isFinal ? <FlagOutlined style={{ color: '#52c41a' }} /> : <BulbOutlined style={{ color: '#faad14' }} />}
                <Text strong style={{ fontSize: 12 }}>
                    {isFinal ? TEXT.finalReasoning : TEXT.reasoning}
                </Text>
            </Space>
            <Paragraph
                style={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    fontSize: 12,
                    color: '#595959',
                    lineHeight: 1.6,
                }}
            >
                {thinking}
            </Paragraph>
        </div>
    );
};


const ToolCallCard = ({ entry, index }) => {
    const failed = !!entry.error;
    return (
        <Card
            size="small"
            style={{ marginBottom: 8, borderColor: failed ? '#ffa39e' : '#d9d9d9' }}
            styles={{ body: { padding: 10 } }}
            title={(
                <Space size={6} wrap>
                    <Badge
                        count={index + 1}
                        style={{ backgroundColor: failed ? '#ff4d4f' : '#1677ff' }}
                    />
                    <ToolOutlined />
                    <Text code style={{ fontSize: 12 }}>{entry.tool_name}</Text>
                    {failed ? (
                        <Tag color="error" icon={<CloseCircleOutlined />} style={{ margin: 0 }}>
                            {TEXT.error}
                        </Tag>
                    ) : (
                        <Tag color="success" icon={<CheckCircleOutlined />} style={{ margin: 0 }}>
                            OK
                        </Tag>
                    )}
                </Space>
            )}
        >
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
                <div>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>
                        {TEXT.arguments}
                    </Text>
                    <CodeBlock value={entry.arguments} maxHeight={120} />
                </div>
                <div>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>
                        {failed ? TEXT.error : TEXT.result}
                    </Text>
                    <CodeBlock
                        value={failed ? entry.error : entry.result}
                        maxHeight={200}
                    />
                </div>
            </Space>
        </Card>
    );
};


const ToolTraceViewer = ({ trace }) => {
    const safeTrace = Array.isArray(trace) ? trace : [];
    const groups = useMemo(() => groupTraceByIteration(safeTrace), [safeTrace]);

    const stats = useMemo(() => {
        const toolCalls = safeTrace.filter((e) => e?.tool_name && e.tool_name !== FINAL_ANSWER_MARKER).length;
        const errors = safeTrace.filter((e) => !!e?.error).length;
        return { iterations: groups.length, toolCalls, errors };
    }, [safeTrace, groups]);

    const allKeys = useMemo(() => groups.map((g) => `iter-${g.iteration}`), [groups]);
    const [expandedKeys, setExpandedKeys] = useState([]);

    const allExpanded = expandedKeys.length === allKeys.length && allKeys.length > 0;
    const handleToggleAll = () => {
        setExpandedKeys(allExpanded ? [] : allKeys);
    };

    if (!safeTrace.length) {
        return (
            <Card
                size="small"
                title={(
                    <Space>
                        <RobotOutlined />
                        <span>{TEXT.title}</span>
                    </Space>
                )}
            >
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={TEXT.empty} />
            </Card>
        );
    }

    return (
        <Card
            size="small"
            title={(
                <Space size={6}>
                    <RobotOutlined />
                    <span>{TEXT.title}</span>
                    <Tag color="blue">{TEXT.iterations}: {stats.iterations}</Tag>
                    <Tag color="geekblue">{TEXT.toolCalls}: {stats.toolCalls}</Tag>
                    {stats.errors > 0 && (
                        <Tag color="error" icon={<CloseCircleOutlined />}>
                            {stats.errors}
                        </Tag>
                    )}
                </Space>
            )}
            extra={(
                <Button type="link" size="small" onClick={handleToggleAll}>
                    {allExpanded ? TEXT.collapseAll : TEXT.expandAll}
                </Button>
            )}
        >
            <Collapse
                ghost
                size="small"
                activeKey={expandedKeys}
                onChange={(keys) => setExpandedKeys(Array.isArray(keys) ? keys : [keys])}
            >
                {groups.map((group, gIdx) => {
                    const color = group.isFinal ? '#52c41a' : (ITERATION_COLORS[group.iteration % ITERATION_COLORS.length]);
                    const headerSuffix = group.isFinal
                        ? <Tag color="success">{TEXT.finalAnswer}</Tag>
                        : null;
                    return (
                        <Panel
                            key={`iter-${group.iteration}`}
                            header={(
                                <Space size={6}>
                                    <Badge color={color} />
                                    <Text strong style={{ fontSize: 13 }}>
                                        {TEXT.iteration} #{group.iteration}
                                    </Text>
                                    {headerSuffix}
                                    {group.calls.length > 0 && (
                                        <Tag color="default" style={{ margin: 0 }}>
                                            {group.calls.length} {TEXT.toolCalls}
                                        </Tag>
                                    )}
                                    {group.thinking && (
                                        <Tooltip title={group.thinking.slice(0, 200) + (group.thinking.length > 200 ? '…' : '')}>
                                            <Tag color="gold" icon={<BulbOutlined />} style={{ margin: 0 }}>
                                                {group.isFinal ? TEXT.finalReasoning : TEXT.reasoning}
                                            </Tag>
                                        </Tooltip>
                                    )}
                                </Space>
                            )}
                        >
                            <Timeline style={{ paddingLeft: 4 }}>
                                <Timeline.Item color={color} dot={<RobotOutlined />}>
                                    <ReasoningBlock thinking={group.thinking} isFinal={group.isFinal} />
                                    {group.calls.length > 0 ? (
                                        group.calls.map((entry, idx) => (
                                            <ToolCallCard
                                                key={`${entry.tool_name}-${entry.iteration}-${idx}`}
                                                entry={entry}
                                                index={idx}
                                            />
                                        ))
                                    ) : group.isFinal ? (
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            （此轮直接给出最终 JSON，无工具调用）
                                        </Text>
                                    ) : null}
                                </Timeline.Item>
                            </Timeline>
                        </Panel>
                    );
                })}
            </Collapse>
        </Card>
    );
};

export default ToolTraceViewer;
